import { createRouter, createWebHistory } from 'vue-router';

import store from '../store';
import { readSession, SESSION_CHANGED } from '../services/core/auth_session';
import { loginLocation, sessionRedirect } from './session_policy';

// import routes
import agraRoutes from './routes/agra.routes';
import kokwatchRoutes from './routes/kokwatch.routes';
import questionnaireRoutes from './routes/questionnaire.routes';
import waterSupplyRoutes from './routes/watersupply.routes';

const routes = [
  ...kokwatchRoutes,
  ...agraRoutes,
  ...questionnaireRoutes,
  ...waterSupplyRoutes,
  {
    path: '/home',
    redirect: '/agra/mysurvey',
    meta: { layout: 'app', sidebar: false, footer: true, authorize: ['member', 'admin'] }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/PageNotFound.vue'),
    meta: { layout: 'auth', sidebar: false, footer: true }
  },
  {
    path: '/testline',
    name: 'testline',
    component: () => import('@/views/testLine.vue'),
    meta: { layout: 'auth', sidebar: false, footer: false }
  },
  {
    path: '/testauth',
    name: 'testauth',
    component: () => import('@/views/testAuth.vue'),
    meta: { layout: 'auth', sidebar: false, footer: false }
  },
  {
    path: '/lineLogin/:topage?',
    name: 'lineLogin',
    props: true,
    component: () => import('@/views/agra/login/LineLogin.vue'),
    meta: { layout: 'auth', sidebar: false, footer: false }
  },
  {
    path: '/testPreviewVideo',
    name: 'testPreviewVideo',
    component: () => import('@/views/testPreviewVideo.vue'),
    meta: { layout: 'auth', sidebar: false, footer: false }
  },
  {
    path: '/testExcel',
    name: 'testExcel',
    component: () => import('@/views/testExcel.vue'),
    meta: { layout: 'auth', sidebar: false, footer: false }
  }
];

const router = new createRouter({
  // mode: 'history',
  history: createWebHistory(),
  linkExactActiveClass: 'active',
  routes,
  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { left: 0, top: 0 };
    }
  }
});

router.beforeEach(async (to, from, next) => {
  const { authorize, screenId, permissionKey, systemName } = to.meta;
  await store.dispatch('auth/syncSession');
  const redirect = sessionRedirect(to, readSession());
  if (redirect) return next(redirect);

  if (authorize?.length) {
    try {
      await store.dispatch('permission/fetchPermissions');
    } catch (error) {
      if (!readSession().token?.accessToken || [401, 403].includes(error?.response?.status)) {
        return next(loginLocation(to));
      }
      return next(error);
    }
    // The session may have changed while permissions were loading.
    const afterFetch = sessionRedirect(to, readSession());
    if (afterFetch) return next(afterFetch);
  }

  if (to.path === '/') {
    return next('/agra');
  }

  if (to.meta && to.meta.layout && to.meta.layout === 'auth') {
    store.commit('setLayout', 'auth');
  } else {
    store.commit('setLayout', 'app');
  }

  if (screenId && screenId.length > 0 && systemName) {
    const hasPermission = store.getters['permission/hasPermissionById'](screenId);
    if (permissionKey?.length > 0) {
      const allFalse = permissionKey.every(key => hasPermission[key] === false);
      if (allFalse) {
        return next(`/${systemName}`);
      }
    }
  }
  next();
});

// localStorage is shared by tabs: react when another tab logs into a different
// system, rather than leaving the old protected screen active.
const syncActiveSession = async () => {
  await store.dispatch('auth/syncSession');
  const current = router.currentRoute.value;
  const redirect = sessionRedirect(current, readSession());
  if (redirect) await router.replace(redirect);
};
window.addEventListener(SESSION_CHANGED, syncActiveSession);
window.addEventListener('storage', event => {
  if (event.key === 'member' || event.key === 'token' || event.key === null) syncActiveSession();
});

export default router;